import React from 'react'
import PropTypes from 'prop-types'
import {values} from 'lodash'
import DialogLogic from './DialogLogic'
import Triangle from '#components/svg/icons/Triangle'
import OverflowStrategy from './OverflowStrategy'
import {FlyoutPosition} from './FlyoutPosition'
import {FlyoutPivot} from './FlyoutPivot'
import {FlyoutOverflowHandling} from './FlyoutOverflowHandling'

export default class Flyout extends React.Component {
  wrapperRef = {
    offsetWidth: 0,
    offsetHeight: 0,
  }

  padding = 10

  constructor(props) {
    super(props)
    this.state = {
      width: props.startWidth,
      height: props.startHeight,
    }
  }

  componentDidMount() {
    const {closeEventName, stayOpenOnOutsideClick} = this.props
    const width = this.wrapperRef.offsetWidth
    const height = this.wrapperRef.offsetHeight
    if (!stayOpenOnOutsideClick || stayOpenOnOutsideClick !== true) {
      document.addEventListener(closeEventName, this.handleClickOutside)
    }
    if (width > 5 && height > 5) {
      this.setState({width, height})
    }
  }

  componentWillUnmount() {
    const {closeEventName, stayOpenOnOutsideClick} = this.props
    if (!stayOpenOnOutsideClick || stayOpenOnOutsideClick !== true) {
      document.removeEventListener(closeEventName, this.handleClickOutside)
    }
  }

  isHorizontal = position => position === FlyoutPosition.right || position === FlyoutPosition.left

  calculateOverflowArrayForPosition = () => {
    const {anchorRect, bounds, flyoutDistance} = this.props
    const {width, height} = this.state
    return [
      {
        ovrfl: (anchorRect.top - height - flyoutDistance - bounds.top) * -1,
        pos: FlyoutPosition.top,
      },
      {
        ovrfl: anchorRect.right + width + flyoutDistance - bounds.right,
        pos: FlyoutPosition.right,
      },
      {
        ovrfl: anchorRect.bottom + height + flyoutDistance - bounds.bottom,
        pos: FlyoutPosition.bottom,
      },
      {
        ovrfl: (anchorRect.left - width - flyoutDistance - bounds.left) * -1,
        pos: FlyoutPosition.left,
      },
    ]
  }

  calculateOverflowAtStart = ({anchorRectBorder, flyoutOverhang, bound}) =>
    (anchorRectBorder - flyoutOverhang - this.padding - bound) * -1

  calculateOverflowAtEnd = ({anchorRectBorder, flyoutOverhang, bound}) =>
    anchorRectBorder + flyoutOverhang + this.padding - bound

  calculateOverflowArrayForPivot = position => {
    const {anchorRect, bounds} = this.props
    const {width, height} = this.state

    const startOverflowParams = {
      anchorRectBorder: this.isHorizontal(position) ? anchorRect.top : anchorRect.left,
      flyoutOverhang: this.isHorizontal(position) ? height - anchorRect.height : width - anchorRect.width,
      bound: this.isHorizontal(position) ? bounds.top : bounds.left,
    }

    const endOverflowParams = {
      anchorRectBorder: this.isHorizontal(position) ? anchorRect.bottom : anchorRect.right,
      flyoutOverhang: this.isHorizontal(position) ? height - anchorRect.height : width - anchorRect.width,
      bound: this.isHorizontal(position) ? bounds.bottom : bounds.right,
    }

    return [
      {
        ovrfl: this.calculateOverflowAtEnd(endOverflowParams),
        piv: FlyoutPivot.start,
      },
      {
        ovrfl: this.calculateOverflowAtStart(startOverflowParams),
        piv: FlyoutPivot.end,
      },
    ]
  }

  findPositionWithSmallestOverflow = () => {
    const {overflowHandling} = this.props
    let {position} = this.props

    const overflows = this.calculateOverflowArrayForPosition()

    // not enough space -> find better position
    // first, check inverse
    if (
      overflowHandling === FlyoutOverflowHandling.inversePosition ||
      overflowHandling === FlyoutOverflowHandling.all
    ) {
      position = OverflowStrategy.inverse(overflows, position)
    }

    // second, check clockwise
    if (overflowHandling === FlyoutOverflowHandling.clockwise || overflowHandling === FlyoutOverflowHandling.all) {
      position = OverflowStrategy.clockwise(overflows, position)
    }

    // third, check for smallest overlap
    if (
      overflowHandling === FlyoutOverflowHandling.minimalOverflow ||
      overflowHandling === FlyoutOverflowHandling.all
    ) {
      position = OverflowStrategy.minOverflow(overflows, position)
    }

    return OverflowStrategy.none(position)
  }

  findPivotWithSmallestOverflow = position => {
    const {pivot} = this.props

    if (pivot === FlyoutPivot.dynamic) return pivot

    const overflows = this.calculateOverflowArrayForPivot(position)

    // if there is enough space, return original
    if (overflows.find(o => o.piv === pivot).ovrfl <= 0) return pivot

    // else, check if other direction is better
    const inversePivot = overflows.find(o => o.piv !== pivot)
    if (inversePivot.ovrfl <= 0) return inversePivot.piv

    // else just return dynamic
    return FlyoutPivot.dynamic
  }

  calculatePositionAndPivotFromBounds = () => {
    const newPosition = this.findPositionWithSmallestOverflow()
    const newPivot = this.findPivotWithSmallestOverflow(newPosition)

    return {newPosition, newPivot}
  }

  refinePosition = newPosition => {
    const {position, anchorRect, bounds} = this.props
    const {width} = this.state
    if (position === newPosition) return {}
    const style = {}

    if (newPosition === FlyoutPosition.right) {
      const overflow = anchorRect.right + width + this.padding - bounds.right
      if (overflow > 0) {
        const overflowPercent = (overflow / anchorRect.width) * 100
        style.left = `${100 - overflowPercent}%`
      }
    } else if (newPosition === FlyoutPosition.left) {
      const overflow = anchorRect.left - width - this.padding - bounds.left
      if (overflow < 0) {
        const overflowPercent = (overflow / anchorRect.width) * 100
        style.right = `${100 + overflowPercent}%`
      }
    }

    return style
  }

  refineDynamicPivotForHorizontalPosition = refinedPositionStyle => {
    const {height} = this.state
    const {anchorRect, bounds} = this.props
    const flyoutOverhang = (height - anchorRect.height) / 2

    const overflowTop = anchorRect.top - flyoutOverhang - this.padding - bounds.top
    const overflowBottom = anchorRect.bottom + flyoutOverhang + this.padding - bounds.bottom

    const halfAnchor = anchorRect.height / 2
    const flyoutMiddle = height / 2 - halfAnchor
    if (overflowTop < 0 && overflowBottom < 0) {
      return {...refinedPositionStyle, top: -flyoutMiddle - overflowTop}
    }
    if (overflowBottom > 0 && overflowTop > 0) {
      return {...refinedPositionStyle, top: -flyoutMiddle - overflowBottom}
    }

    return {...refinedPositionStyle, top: -flyoutMiddle}
  }

  refineDynamicPivotForVerticalPosition = refinedPositionStyle => {
    const {width} = this.state
    const {anchorRect, bounds} = this.props
    const flyoutOverhang = (width - anchorRect.width) / 2

    const overflowLeft = anchorRect.left - flyoutOverhang - this.padding - bounds.left
    const overflowRight = anchorRect.right + flyoutOverhang + this.padding - bounds.right

    const halfAnchor = anchorRect.width / 2
    const flyoutMiddle = width / 2 - halfAnchor
    if (overflowLeft < 0 && overflowRight < 0) {
      return {...refinedPositionStyle, left: -flyoutMiddle - overflowLeft}
    }
    if (overflowRight > 0 && overflowLeft > 0) {
      return {...refinedPositionStyle, left: -flyoutMiddle - overflowRight}
    }

    return {...refinedPositionStyle, left: -flyoutMiddle}
  }

  refinePivot = (newPosition, newPivot, refinedPositionStyle) => {
    const {anchorRect} = this.props

    if (this.isHorizontal(newPosition)) {
      if (newPivot === FlyoutPivot.start) {
        return {...refinedPositionStyle, top: anchorRect.height / 2 - 4}
      }
      if (newPivot === FlyoutPivot.end) {
        return {...refinedPositionStyle, bottom: anchorRect.height / 2 - 4}
      }
      return this.refineDynamicPivotForHorizontalPosition(refinedPositionStyle)
    }
    if (newPivot === FlyoutPivot.start) {
      const centered = anchorRect.width / 2 - 4
      return {...refinedPositionStyle, left: centered < 20 ? centered : 20}
    }
    if (newPivot === FlyoutPivot.end) {
      const centered = anchorRect.width / 2 - 4
      return {refinedPositionStyle, right: centered < 20 ? centered : 20}
    }
    return this.refineDynamicPivotForVerticalPosition(refinedPositionStyle)
  }

  applyCustomDistance = (newPosition, refinedPositionStyle) => {
    const {flyoutDistance} = this.props
    switch (newPosition) {
      case FlyoutPosition.right:
        return {...refinedPositionStyle, marginLeft: flyoutDistance}
      case FlyoutPosition.left:
        return {...refinedPositionStyle, marginRight: flyoutDistance}
      case FlyoutPosition.top:
        return {...refinedPositionStyle, marginBottom: flyoutDistance}
      case FlyoutPosition.bottom:
        return {...refinedPositionStyle, marginTop: flyoutDistance}
      default:
        return refinedPositionStyle
    }
  }

  calculateArrowDynamicStyle = (refinedPositionStyle, position, pivot) => {
    const {anchorRect, showArrow} = this.props
    const arrowStyle = showArrow ? 'block' : 'none'

    if (this.isHorizontal(position) && pivot === FlyoutPivot.dynamic) {
      const halfAnchor = anchorRect.height / 2
      return {top: refinedPositionStyle.top * -1 + halfAnchor - 5, display: arrowStyle}
    }
    if (pivot === FlyoutPivot.dynamic) {
      const halfAnchor = anchorRect.width / 2
      return {left: refinedPositionStyle.left * -1 + halfAnchor - 9, display: arrowStyle}
    }
    return {display: arrowStyle}
  }

  handleClickOutside = event => {
    const {anchorRef} = this.props
    if (DialogLogic.containsParentElementInTree(anchorRef, event.target)) {
      event.preventDefault()
      event.stopPropagation()
      return
    }
    DialogLogic.handleClickOutside(event, this.wrapperRef, this.closeDialog)
  }

  closeDialog = evt => {
    const {closed} = this.props
    closed(evt)
  }

  render() {
    const {children, open, borderColor, backgroundColor} = this.props
    const {newPosition, newPivot} = this.calculatePositionAndPivotFromBounds()

    let refinedPositionStyle = this.refinePosition(newPosition)
    refinedPositionStyle = this.refinePivot(newPosition, newPivot, refinedPositionStyle)
    refinedPositionStyle = this.applyCustomDistance(newPosition, refinedPositionStyle)
    refinedPositionStyle = {...refinedPositionStyle, borderColor, background: backgroundColor}

    const arrowPositionExtraStyle = this.calculateArrowDynamicStyle(refinedPositionStyle, newPosition, newPivot)
    const classes = [open ? 'Open' : '', 'Flyout']
    return (
      <div
        className={classes.join(' ')}
        style={refinedPositionStyle}
        pos={newPosition}
        piv={newPivot}
        ref={node => {
          this.wrapperRef = node
        }}>
        <div className="arrowPosition" style={arrowPositionExtraStyle}>
          <Triangle borderColor={borderColor} triangleColor={backgroundColor} />
        </div>
        {children}
      </div>
    )
  }
}

Flyout.defaultProps = {
  children: [],
  flyoutDistance: 2,
  closeEventName: 'mouseup',
  bounds: {
    top: 0,
    left: 0,
    right: window.offsetWidth,
    bottom: window.offsetHeight,
    width: window.offsetWidth,
    height: window.offsetHeight,
  },
  startWidth: 0,
  startHeight: 0,
  overflowHandling: FlyoutOverflowHandling.all,
}

Flyout.propTypes = {
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array, PropTypes.node]),
  closeEventName: PropTypes.string,
  position: PropTypes.oneOf(values(FlyoutPosition)),
  pivot: PropTypes.oneOf(values(FlyoutPivot)),
  flyoutDistance: PropTypes.number,
  open: PropTypes.bool,
  closed: PropTypes.func,
  bounds: PropTypes.shape({
    top: PropTypes.number,
    left: PropTypes.number,
    right: PropTypes.number,
    bottom: PropTypes.number,
  }),
  anchorRect: PropTypes.shape({
    top: PropTypes.number,
    left: PropTypes.number,
    right: PropTypes.number,
    bottom: PropTypes.number,
    height: PropTypes.number,
    width: PropTypes.number,
  }),
  borderColor: PropTypes.string,
  backgroundColor: PropTypes.string,
  showArrow: PropTypes.bool,
  startWidth: PropTypes.number,
  startHeight: PropTypes.number,
  overflowHandling: PropTypes.oneOf(values(FlyoutOverflowHandling)),
  anchorRef: PropTypes.object,
  stayOpenOnOutsideClick: PropTypes.bool,
}
